import { forwardRef, motion, useTheme } from '@hover/blueprint';
import type { LoadableComponent } from '@loadable/component';
import loadable from '@loadable/component';
import { AnimatePresence } from 'framer-motion';
import React, { useRef } from 'react';
import type { RouteProps } from 'react-router';
import { Route, Switch, useHistory } from 'react-router';

import type { MetaProps } from 'src/modules/app/components/Meta/Meta';
import { Meta } from 'src/modules/app/components/Meta/Meta';
import { Hotjar } from 'src/modules/homeownerApp/components/Hotjar';
import { useHomeownerAppProfileQuery } from 'src/modules/homeownerApp/graphql/generated';
import { HomeownerMeasurements } from 'src/modules/homeownerMeasurements';
import { SetIntercomVisibility } from 'src/shared/components/SetIntercomVisibility';
import {
  TranslationNamespace,
  useIsEmbed,
  useTranslate,
} from 'src/shared/hooks';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { Route as Routes } from 'src/shared/route';
import { exitStyles, presenceVariants } from 'src/shared/utils/motion';

import type { HomeownerRouteDescriptor } from '../../constants';
import type { RouteAnimationCustom, RouteAnimationProps } from '../../helpers';
import { getRouteAnimation, getRouteExitAnimation } from '../../helpers';
import { RoutesProvider, useHomeownerAppContext } from '../../hooks';
import { useDegradedPerformanceToast } from '../../hooks/useDegradedPerformanceToast';
import { useFullscreenDepth } from '../FullscreenImage';
import { Layout, RouteAwareLayout } from '../Layout/Layout';
import { NotFoundSplash } from '../errors';

const EmbedContainer = loadable(
  () => import('../Embed/components/EmbedContainer'),
  {
    resolveComponent: module => module.EmbedContainer,
  },
);

const EmbedProjectPage = loadable(
  () => import('../Embed/components/EmbedProjectPage'),
  {
    resolveComponent: module => module.EmbedProjectRoute,
  },
);

const Home = loadable(() => import('../Home'), {
  resolveComponent: module => module.Home,
});

const LoadingScreen = loadable(
  () => import('../OriginalImageScreen/LoadingScreen'),
  {
    resolveComponent: module => module.LoadingScreen,
  },
);

const Share = loadable(() => import('../Share'), {
  resolveComponent: module => module.Share,
});

const OriginalImageScreen = loadable(() => import('../OriginalImageScreen'), {
  resolveComponent: module => module.OriginalImageScreen,
});

const Customize = loadable(() => import('../Customize'), {
  resolveComponent: module => module.CustomizeRoute,
});

const CustomizeUpload = loadable(() => import('../Customize'), {
  resolveComponent: module => module.CustomizeUpload,
});

const Project = loadable(() => import('../Projects'), {
  resolveComponent: module => module.ProjectRoute,
});

const ProjectsList = loadable(() => import('../Projects'), {
  resolveComponent: module => module.ProjectsList,
});

const ProjectCreation = loadable(() => import('../Projects'), {
  resolveComponent: module => module.ProjectCreation,
});

const AiDisplayImageShare = loadable(
  () => import('../AiDisplayImageShare/AiDisplayImageShare'),
  {
    resolveComponent: module => module.AiDisplayImageShare,
  },
);

/**
 * Homeowner route mapping. A route must be defined here in order for
 * it to be preloaded using the `preload` function from `useRoutes`.
 */
const routes = {
  embedContainer: {
    exact: true,
    path: Routes.EMBED.path,
    component: EmbedContainer,
  },
  embedProjectPage: {
    exact: true,
    path: Routes.EMBED_PROJECT.path,
    component: EmbedProjectPage,
  },
  home: {
    exact: true,
    path: [Routes.HOMEOWNER_APP_HOME.path, Routes.AI_DESIGNER.path],
    component: Home,
  },
  loadingScreen: {
    exact: true,
    path: Routes.HOMEOWNER_APP_HOME.path,
    component: LoadingScreen,
  },
  share: {
    exact: true,
    path: [Routes.HOMEOWNER_APP_SHARE.path, Routes.AI_DESIGNER_SHARE.path],
    component: Share,
  },
  original: {
    exact: true,
    path: [
      Routes.HOMEOWNER_APP_ORIGINAL.path,
      Routes.HOMEOWNER_APP_ORIGINAL_DISPLAY_FULLSCREEN.path,
      Routes.AI_DESIGNER_INSPIRATIONS.path,
    ],
    component: OriginalImageScreen,
  },
  projectIdeas: {
    exact: true,
    path: [
      Routes.HOMEOWNER_APP_PROJECT_IDEAS.path,
      Routes.HOMEOWNER_APP_PROJECT_IDEAS_FULLSCREEN.path,
    ],
    component: OriginalImageScreen,
  },
  customizeUpload: {
    exact: true,
    path: Routes.HOMEOWNER_APP_CUSTOMIZE_UPLOAD.path,
    component: CustomizeUpload,
  },
  customize: {
    exact: true,
    path: [
      Routes.HOMEOWNER_APP_CUSTOMIZE.path,
      Routes.HOMEOWNER_APP_CUSTOMIZE_NEW.path,
      Routes.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path,
    ],
    component: Customize,
  },
  projectCreation: {
    exact: true,
    path: Routes.HOMEOWNER_APP_NEW_PROJECT.path,
    component: ProjectCreation,
  },
  project: {
    exact: true,
    path: [
      Routes.HOMEOWNER_APP_PROJECT.path,
      Routes.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
    ],
    component: Project,
  },
  projectsList: {
    exact: true,
    path: Routes.HOMEOWNER_APP_PROJECTS_LIST.path,
    component: ProjectsList,
  },
  displayImageShare: {
    exact: true,
    path: [
      Routes.DISPLAY_IMAGE_SHARE_FULLSCREEN.path,
      Routes.DISPLAY_IMAGE_SHARE.path,
    ],
    component: AiDisplayImageShare,
  },
  homeownerMeasurements: {
    exact: true,
    path: [Routes.HOMEOWNER_MEASUREMENTS.path],
    component: HomeownerMeasurements,
  },
} satisfies Record<string, HomeownerRouteDescriptor>;

interface HomeownerRouteProps
  extends RouteProps,
    MetaProps,
    Omit<RouteAnimationProps, 'mode'> {
  className?: string;
  component?: LoadableComponent<{}>;
  fullBleed?: boolean;
}

const HomeownerRoute = forwardRef<HomeownerRouteProps, 'div'>(
  (
    {
      className,
      containerProps = {},
      component: Component,
      children,
      shouldAnimate,
      title,
      ...props
    },
    ref,
  ) => {
    const {
      motion: { framer },
    } = useTheme();

    const isEmbed = useIsEmbed();
    return (
      <motion.div
        className={className}
        height={isEmbed ? '100vh' : undefined}
        marginX="auto"
        overflow={isEmbed ? 'hidden' : undefined}
        position="relative"
        {...presenceVariants({
          // This is the default route transition. See `./helpers/routes.ts` for
          // how to specify transitions for specific route changes.
          initial: shouldAnimate ? { opacity: 0, y: 50 } : {},
          animate: shouldAnimate ? { opacity: 1, y: 0 } : {},
          exit: (custom: RouteAnimationCustom | undefined) => {
            if (!custom) return {};

            return getRouteExitAnimation(custom.from, custom.to);
          },
        })}
        transition={framer.tight}
        {...containerProps}
        ref={ref}
      >
        <Route {...props}>
          <>
            <Meta title={title} />
            {!isEmbed && <Hotjar />}
            <RouteAwareLayout />
            {Component ? <Component /> : children}
          </>
        </Route>
      </motion.div>
    );
  },
);

const Router = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { currentMenu } = useHomeownerAppContext();
  const { homeownerAppUploadFile } = useGlobalStorageContext();
  const { data: profileData } = useHomeownerAppProfileQuery();

  const loggedIn = !!profileData?.profile?.user;

  const { location } = useHistory();
  const { pathname } = location;

  // We need to store the previous route so we know where we
  // came from, but we want to avoid triggering a re-render
  const previousPathnameRef = useRef(pathname);

  const { current: previousPath } = previousPathnameRef;

  const { mode, ...animationProps } = getRouteAnimation(previousPath, pathname);
  const { shouldAnimate } = animationProps;

  const routeProps = { ...animationProps, pathname };
  const custom: RouteAnimationCustom = {
    to: pathname,
    from: previousPath,
    shouldAnimate,
  };

  previousPathnameRef.current = pathname;

  // We use the key to force a remount of the route to trigger the
  // `AnimatePresence` animation. Preserve the same `key` using a ref until we
  // hit a route change that should actually be animated.
  const keyRef = useRef(0);

  if (shouldAnimate) keyRef.current += 1;

  // If we have a file to upload on sign in, we need to redirect to upload
  const getHomeRoute = () => {
    if (loggedIn) {
      if (homeownerAppUploadFile) {
        return routes.loadingScreen;
      }
      return routes.home;
    }
    return currentMenu ? routes.loadingScreen : routes.home;
  };

  const customizeExitDepth = useFullscreenDepth() + 1;

  useDegradedPerformanceToast();

  return (
    <RoutesProvider routes={routes}>
      <Layout
        contentProps={{
          position: 'relative',
          sx: exitStyles('.customize-route', { zIndex: customizeExitDepth }),
        }}
      >
        <SetIntercomVisibility visible={false} />
        <AnimatePresence custom={custom} initial={false} mode={mode}>
          {/**
           * We must pass both `key` and `location` to `Switch` in order
           * for the `AnimatePresence` animations to work correctly
           *
           * @see {@link https://github.com/framer/motion/issues/650}
           */}
          <Switch key={keyRef.current} location={location}>
            <HomeownerRoute {...routes.embedContainer} {...routeProps} />
            <HomeownerRoute {...routes.embedProjectPage} {...routeProps} />
            <HomeownerRoute
              title={t('meta.share')}
              {...routes.share}
              {...routeProps}
            />
            <HomeownerRoute {...routes.original} {...routeProps} />
            <HomeownerRoute {...routes.projectIdeas} {...routeProps} />
            <HomeownerRoute fullBleed {...getHomeRoute()} {...routeProps} />
            <HomeownerRoute {...routes.customizeUpload} {...routeProps} />
            <HomeownerRoute
              className="customize-route"
              {...routes.customize}
              {...routeProps}
            />
            <HomeownerRoute {...routes.displayImageShare} {...routeProps} />
            <HomeownerRoute {...routes.projectCreation} {...routeProps} />
            <HomeownerRoute {...routes.project} {...routeProps} />
            <HomeownerRoute {...routes.projectsList} {...routeProps} />
            <HomeownerRoute {...routes.homeownerMeasurements} {...routeProps} />
            <HomeownerRoute key="404" path="*" shouldAnimate={shouldAnimate}>
              <NotFoundSplash />
            </HomeownerRoute>
          </Switch>
        </AnimatePresence>
      </Layout>
    </RoutesProvider>
  );
};

export { Router };
